import { useIntl } from 'react-intl';
import moment from 'moment';
import { Space, Tag, Button, Popover, Tooltip } from 'antd';

import { WorkorderColumn } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.types';
import { useWorkorderConfigurationProfile } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';

import {
  ComplementOriginEnum,
  ComplementOriginTranslator,
  ListWorkOrdersResult,
  WorkOrderOrderColumnTypeNew,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';

import { ColumnsType, ColorSwatch, Group } from '@/components/_common';
import EditIcon from '@/components/_common/EditIcon';
import StatusTag from '@/components/_common/StatusTag';
import {
  workorderComplementColor,
  WorkOrderComplementStatus,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import WorkOrderActionDropdown from '../WorkOrderActionDropdown';
import Authorities from '@/types/Authority';
import { getUserFromLocalStorage } from '@/utils/user';
import { FaPercent } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import formatterPrice from '@/utils/number/formatterPrice';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import WorkOrderStatusTag from '@/components/_common/WorkOrderStatusTag';
import { CheckCircle } from 'react-feather';
import { isAppPortocar } from '@/index';
import { useCallback, useMemo } from 'react';

interface Props {
  hasSorter?: boolean;
  withAction?: boolean;
  orderColumns?: string;
}

interface useWorkOrderColumnReturn {
  columns: ColumnsType<ListWorkOrdersResult>;
  isFetching: boolean;
}

function useWorkOrdersColumns(props?: Props): useWorkOrderColumnReturn {
  const withAction =
    typeof props?.withAction === 'boolean' ? props.withAction : true;

  const hasSorter = useMemo(
    () => props?.hasSorter ?? { multiple: 1 },
    [props?.hasSorter],
  );

  const intl = useIntl();

  const { employeeId } = getUserFromLocalStorage();

  const history = useHistory();

  const { data, isFetching } = useWorkorderConfigurationProfile();

  const actionCommissionActive = data?.data.find(
    (profile) => profile.column === WorkorderColumn.Commission,
  )?.active;

  const getSortOrder = useCallback(
    (key: WorkOrderOrderColumnTypeNew) => {
      if (!hasSorter) return null;

      const orderColumn = props?.orderColumns
        ?.split(',')
        .find((orderColumn) => orderColumn.includes(key));

      if (orderColumn) {
        return orderColumn.includes(':1') ? 'descend' : 'ascend';
      }

      return null;
    },
    [props?.orderColumns, hasSorter],
  );

  const columns: ColumnsType<ListWorkOrdersResult> = [
    {
      key: WorkorderColumn.Tag,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Tag}`,
      }),
      ellipsis: true,
      width: 70,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.tag),
      render: (_, row) =>
        !!row.tag && (
          <Group position="center">
            <Tooltip
              title={row.tag?.name?.toUpperCase()}
              color={row.tag?.color}
              placement="right"
            >
              <div>
                <ColorSwatch backgroundColor={row.tag?.color} />
              </div>
            </Tooltip>
          </Group>
        ),
    },
    {
      key: WorkorderColumn.activityStatus,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.activityStatus}`,
      }),
      ellipsis: true,
      width: 150,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.situation),
      render: (_, row) =>
        !!row.activityStatus && (
          <Tooltip
            title={row.activityStatus?.name}
            color={row.activityStatus?.color}
            placement="right"
          >
            <WorkOrderStatusTag
              color={row.activityStatus?.color}
              label={row.activityStatus?.name}
            />
          </Tooltip>
        ),
    },
    {
      key: WorkorderColumn.Number,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.work_order_number),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Number}`,
      }),
      dataIndex: 'complementIdentifier',
      ellipsis: true,
      width: 100,
    },
    {
      key: WorkorderColumn.PortoBattery,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.is_porto_battery),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.PortoBattery}`,
      }),
      dataIndex: 'isPortoBatteryWorkOrder',
      render: (value) =>
        value ? <CheckCircle color="green" size={18} /> : null,
      align: 'center',
      width: 150,
    },
    {
      key: WorkorderColumn.Company,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.company),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Company}`,
      }),
      dataIndex: ['company', 'name'],
      ellipsis: true,
      width: 120,
    },
    {
      key: WorkorderColumn.Estimator,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.estimator),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Estimator}`,
      }),
      dataIndex: ['estimator', 'name'],
      ellipsis: true,
      width: 120,
    },
    {
      key: WorkorderColumn.Customer,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.customer),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Customer}`,
      }),
      dataIndex: ['customer', 'name'],
      ellipsis: true,
      width: 120,
    },
    {
      key: WorkorderColumn.Insurer,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.insurer),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Insurer}`,
      }),
      dataIndex: ['insurer', 'name'],
      ellipsis: true,
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      key: WorkorderColumn.Prisma,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.prisma),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Prisma}`,
      }),
      dataIndex: 'prisma',
      ellipsis: true,
      width: 120,
    },
    {
      key: WorkorderColumn.fleet,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.fleet),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.fleet}`,
      }),
      dataIndex: 'fleet',
      ellipsis: true,
      width: 75,
      render: (value: string) => value || '-',
    },
    {
      key: WorkorderColumn.Chassi,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.chassi),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Chassi}`,
      }),
      dataIndex: 'chassi',
      ellipsis: true,
      width: 100,
    },
    {
      key: WorkorderColumn.Motorization,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.motorization),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Motorization}`,
      }),
      dataIndex: 'motorization',
      ellipsis: true,
      width: 120,
    },
    {
      key: WorkorderColumn.Plate,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.license_plate),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Plate}`,
      }),
      dataIndex: 'licensePlate',
      ellipsis: true,
      width: 75,
      render: (value: string) => value || '-',
    },
    {
      key: WorkorderColumn.Vehicle,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.vehicle),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Vehicle}`,
      }),
      dataIndex: 'vehicle',
      ellipsis: true,
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      key: WorkorderColumn.EntryDate,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.entry_date),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.EntryDate}`,
      }),
      dataIndex: 'entryDate',
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
      width: 100,
    },
    {
      key: WorkorderColumn.EstimatedLeaveDate,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.estimated_leave_date),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.EstimatedLeaveDate}`,
      }),
      dataIndex: 'estimatedLeaveDate',
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
      width: 100,
    },
    {
      key: WorkorderColumn.AuthorizationDate,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.authorization_date),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.AuthorizationDate}`,
      }),
      dataIndex: 'authorizationDate',
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
      width: 100,
    },
    {
      key: WorkorderColumn.CloseDate,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.closing_date),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.CloseDate}`,
      }),
      dataIndex: 'closingDate',
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
      width: 100,
    },
    {
      key: WorkorderColumn.ReturnDate,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.return_date),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.ReturnDate}`,
      }),
      dataIndex: 'returnDate',
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
      width: 100,
    },
    {
      key: WorkorderColumn.Cost,
      title: `${intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Cost}`,
      })}($)`,
      dataIndex: 'price',
      render: (value: number) =>
        typeof value === 'number' ? formatterPrice(value) : '-',
      width: 100,
    },
    {
      key: WorkorderColumn.Status,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.status),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Status}`,
      }),
      dataIndex: 'status',
      render: (value: WorkOrderComplementStatus) => (
        <StatusTag
          color={workorderComplementColor[value]}
          label={intl.formatMessage({ id: `workOrders.${value}` })}
        />
      ),
      ellipsis: true,
      width: 90,
    },
    {
      key: WorkorderColumn.Type,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.type),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Type}`,
      }),
      dataIndex: 'type',
      render: (value) => (
        <Tag color="processing">
          {intl.formatMessage({ id: `workOrders.${value}` })}
        </Tag>
      ),
      ellipsis: true,
      width: 150,
    },
    {
      key: WorkorderColumn.ComplementOrigin,
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.complement_origin),
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.ComplementOrigin}`,
      }),
      dataIndex: 'complementOrigin',
      ellipsis: true,
      width: 150,
      render: (value: ComplementOriginEnum) =>
        value ? ComplementOriginTranslator[value] : '-',
    },
    {
      key: WorkorderColumn.PortoBatteryWarranty,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.PortoBatteryWarranty}`,
      }),
      dataIndex: 'batteryWarrantyRegistered',
      render: (value) =>
        value ? <CheckCircle color="green" size={18} /> : null,
      align: 'center',
      width: 150,
    },
    {
      key: WorkorderColumn.PortoScheduleDate,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.PortoScheduleDate}`,
      }),
      sorter: hasSorter,
      sortOrder: getSortOrder(WorkOrderOrderColumnTypeNew.schedule_date),
      dataIndex: 'scheduleDate',
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
      width: 100,
    },
    {
      key: 'actions',
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      render: (row, record) => {
        const { hasCommission, workOrderId, complementId } = row;

        return (
          <Space>
            {actionCommissionActive && hasCommission && (
              <Popover
                content="Esse orçamento contem comissão"
                className="edit-icon"
              >
                <Button
                  type="link"
                  icon={<FaPercent color={'#2ac72a'} />}
                  size="small"
                  onClick={() =>
                    history.push(
                      `/app/movements/workOrders/${workOrderId}/complements/${complementId}/commissions`,
                    )
                  }
                />
              </Popover>
            )}

            <WorkOrderActionDropdown workorder={record} />

            <EditIcon
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_VIEW,
                  Authorities.MOVIMENTATION_WORKORDER_EDIT,
                ]) ||
                (record.estimator.id !== Number(employeeId) &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_EDIT_OF_OTHER_BUDGETIST,
                  ]))
              }
              redirect={`/app/movements/workOrders/${record.workOrderId}/complements/${record.complementId}/generalInfo`}
            />
          </Space>
        );
      },
      ellipsis: true,
      width: 100,
      fixed: 'right',
      align: 'right',
    },
  ];

  const columnsDisabled = isAppPortocar
    ? new Set<WorkorderColumn>()
    : new Set<WorkorderColumn>([
        WorkorderColumn.PortoBattery,
        WorkorderColumn.PortoBatteryWarranty,
        WorkorderColumn.PortoScheduleDate,
      ]);

  const activeColumns = columns
    .filter((item) => !columnsDisabled.has(item.key as WorkorderColumn))
    .filter((item) =>
      data?.data.some(
        (a) => (a.active && item.key === a.column) || item.key === 'actions',
      ),
    )
    .filter((item) => withAction || item.key !== 'actions');

  return { isFetching, columns: activeColumns };
}

export default useWorkOrdersColumns;
